import React, { useState, useEffect } from 'react';
import { PreviewContent } from '@/utils/chatContentUtils';
import { Skeleton } from '@/components/ui/skeleton';
import { useIsMobile } from '@/hooks/use-mobile';
import ReactMarkdown from 'react-markdown';
import { cleanMarkdownBold } from '@/lib/utils'; // Import the utility function

interface ChatPreviewContentProps {
  preview: PreviewContent;
  loading?: boolean;
}

const ChatPreviewContent = ({ preview, loading }: ChatPreviewContentProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const isMobile = useIsMobile();
  
  // Faster loading with no delay to display content immediately
  useEffect(() => {
    setShouldLoad(true);
  }, []);

  if (loading) {
    return (
      <div className="w-full h-32 flex flex-col justify-center space-y-2">
        <Skeleton className="h-4 w-full animate-pulse" />
        <Skeleton className="h-4 w-3/4 animate-pulse" />
        <Skeleton className="h-4 w-1/2 animate-pulse" />
      </div>
    );
  }
  
  if ((preview.type === 'image' || preview.type === 'canvas')) {
    return (
      <div className="w-full h-32 overflow-hidden rounded-lg bg-gray-100 flex items-center justify-center">
        {!imageLoaded && <Skeleton className="w-full h-full absolute animate-pulse" />}
        <img 
          src={preview.content} 
          alt="Preview" 
          className={`w-full h-full object-contain rounded-lg transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          loading="lazy"
          onLoad={() => setImageLoaded(true)}
          decoding="async"
          width="300"
          height="150"
        />
      </div>
    );
  } 
  
  if (preview.type === 'code') {
    return (
      <div className="w-full h-32 overflow-hidden rounded-md bg-muted/80 p-2 font-mono text-xs">
        <div className="bg-muted px-2 py-1 rounded-sm mb-1 text-xs font-medium">{preview.name}</div>
        <div className="h-[74px] overflow-hidden">
          {/* Reduce the amount of code shown on mobile */}
          {isMobile ? preview.content.substring(0, 80) : preview.content.substring(0, 150)}
          {preview.content.length > (isMobile ? 80 : 150) && '...'}
        </div>
      </div>
    );
  }
  
  // Show less text on mobile but use ReactMarkdown for formatting
  const textLimit = isMobile ? 120 : 200;
  let displayContent = preview.content || "No messages yet";
  
  // Stop displaying text after the first line break
  const firstNewlineIndex = displayContent.indexOf('\n');
  if (firstNewlineIndex !== -1) {
    displayContent = displayContent.substring(0, firstNewlineIndex);
  }

  if (displayContent.length > textLimit) {
    displayContent = `${displayContent.substring(0, textLimit)}...`;
  }
  
  return (
    <div className="text-sm text-muted-foreground prose dark:prose-invert prose-sm min-h-[90px] max-w-full">
      <div className="line-clamp-4">
        <ReactMarkdown>
          {cleanMarkdownBold(displayContent)}
        </ReactMarkdown>
      </div>
    </div>
  );
};

export default React.memo(ChatPreviewContent);